/**
 * =====================================================================
 * UNIFIED FIXED ASSETS PAGE
 * =====================================================================
 *
 * Consolidates three separate pages into one:
 * 1. Fixed Assets Management (CRUD operations)
 * 2. Post Depreciation (Monthly depreciation posting)
 * 3. Asset Reports (4 report types)
 * 4. Automation Settings (NEW - Auto-depreciation configuration)
 *
 * This provides a single, unified interface for all asset-related tasks.
 * =====================================================================
 */

import React, { useState, useEffect } from 'react'
import { AdminUser } from '@/lib/authService'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Package, TrendingDown, FileText, Settings as SettingsIcon } from 'lucide-react'

// Import tab components (we'll create these next)
import AssetsTab from '@/components/finance/fixed-assets/AssetsTab'
import DepreciationTab from '@/components/finance/fixed-assets/DepreciationTab'
import ReportsTab from '@/components/finance/fixed-assets/ReportsTab'
import AutomationTab from '@/components/finance/fixed-assets/AutomationTab'

interface FixedAssetsUnifiedProps {
  user: AdminUser | null
}

export default function FixedAssetsUnified({ user }: FixedAssetsUnifiedProps) {
  const [activeTab, setActiveTab] = useState('assets')
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Trigger refresh across tabs when data changes
  const handleDataChange = () => {
    setRefreshTrigger(prev => prev + 1)
  }

  if (!user?.churchId) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="w-[400px]">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You must be logged in and associated with a church to access fixed assets.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Fixed Assets Management</h1>
            <p className="text-sm text-muted-foreground">
              Complete asset lifecycle management with automated depreciation (FASB ASC 958 & IAS 16 compliant)
            </p>
          </div>
        </div>
      </div>

      {/* Main Tabbed Interface */}
      <div className="flex-1 overflow-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          <div className="border-b px-6 pt-4">
            <TabsList className="grid w-full max-w-[800px] grid-cols-4">
              <TabsTrigger value="assets" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                <span className="hidden sm:inline">Assets</span>
              </TabsTrigger>
              <TabsTrigger value="depreciation" className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />
                <span className="hidden sm:inline">Depreciation</span>
              </TabsTrigger>
              <TabsTrigger value="reports" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Reports</span>
              </TabsTrigger>
              <TabsTrigger value="automation" className="flex items-center gap-2">
                <SettingsIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Automation</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-auto">
            {/* Tab 1: Assets Management */}
            <TabsContent value="assets" className="h-full m-0 p-6">
              <AssetsTab
                user={user}
                refreshTrigger={refreshTrigger}
                onDataChange={handleDataChange}
              />
            </TabsContent>

            {/* Tab 2: Post Depreciation */}
            <TabsContent value="depreciation" className="h-full m-0 p-6">
              <DepreciationTab
                user={user}
                refreshTrigger={refreshTrigger}
                onDataChange={handleDataChange}
              />
            </TabsContent>

            {/* Tab 3: Reports */}
            <TabsContent value="reports" className="h-full m-0 p-6">
              <ReportsTab
                user={user}
                refreshTrigger={refreshTrigger}
              />
            </TabsContent>

            {/* Tab 4: Automation Settings */}
            <TabsContent value="automation" className="h-full m-0 p-6">
              <AutomationTab
                user={user}
                refreshTrigger={refreshTrigger}
                onDataChange={handleDataChange}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  )
}
